import type { EligibleProduct } from "../types";

/**
 * RDESQ-1988 — Clark Smart Offer Pilot cohort content overrides.
 *
 * When the `Sales-isUsingMovedDiscoveryAndEngagementQuestions` flag is on,
 * swap the en-US `phrase` (and Discovery `examples`) on matched checklist
 * items for the SKUs in `OVERRIDE_SKUS`. Spanish (es-US) copy is intentionally
 * left untouched.
 *
 * Engagement removal is handled separately in `usePushDiscoveryEngagementToTimeline`
 * (drop the engagement filter clause). The legacy checklist UI already hides
 * discovery + engagement sections when the flag is on.
 *
 * Temporary: this lives in code for speed/clarity during the pilot. Once the
 * pilot graduates, fold the new copy into Contentful and delete this module.
 */

const OVERRIDE_LOCALE = "en-US";

type ItemOverride = {
    phrase?: string;
    examples?: string[];
};

const SHARED_OVERRIDES: Record<string, ItemOverride> = {
    DISCOVERY_QUESTION: {
        phrase: "Ask no more than two discovery questions during the Solve",
        examples: [
            "“What other devices around your home are connected or used with your phone?”",
            "“How many wi-fi connected devices do you have in your home such as TVs or laptops?”",
            "“If one of those main devices stopped working, how disruptive would that be for you or your family?”",
            "“Do you currently have protection on any of your other home electronics?”",
            "“Are your devices mostly helping with work, staying connected, entertainment, or a little of everything?”",
        ],
    },
    NON_PARTNER_DEVICES: {
        phrase: "“This plan covers breakdowns on all eligible products for one monthly price no matter when or where they were purchased.”",
    },
    FUTURE_DEVICES: {
        phrase: "“The plan covers products you currently own and future purchases of eligible devices, no receipts or registration required.”",
    },
    PROTECTION: {
        phrase: "“If you do file a claim, a service fee of $0 to no more than $99 plus tax is required per claim, and your home tech will be repaired, replaced, or reimbursed.”",
    },
    COVERAGE: {
        phrase: "“The plan is $25 plus tax per month and again covers all eligible devices in your home.”",
    },
};

const OVERRIDES_BY_SKU: Record<string, Record<string, ItemOverride>> = {
    "3002": {
        ...SHARED_OVERRIDES,
        TRANSITION: {
            phrase: "“I’m glad we resolved the issue. As I mentioned earlier, the Verizon Home Device Protect program is separate from your mobile device insurance, and you’re not currently enrolled. We highly recommend this coverage for customers who regularly use home tech.”",
        },
    },
    HOMEPRO: {
        ...SHARED_OVERRIDES,
        TRANSITION: {
            phrase: "“I’m glad we resolved the issue. As I mentioned earlier, the Home Tech Protection program is separate from your mobile device insurance, and you’re not currently enrolled. We highly recommend this coverage for customers who regularly use home tech.”",
        },
    },
};

export function applyCohortContentOverrides(products: EligibleProduct[], isCohortFlagOn: boolean): EligibleProduct[] {
    if (!isCohortFlagOn) return products;

    return products.map((product) => {
        const overrides = OVERRIDES_BY_SKU[product.productSku];
        if (!overrides) return product;

        const localizedInfo = product.productInfo?.[OVERRIDE_LOCALE];
        if (!localizedInfo?.salesOfferChecklist) return product;

        const updatedSections = localizedInfo.salesOfferChecklist.checklistSections.map((section) => ({
            ...section,
            checklistItems: section.checklistItems.map((item) => {
                const override = overrides[item.key];
                if (!override) return item;
                return {
                    ...item,
                    ...(override.phrase !== undefined && { phrase: override.phrase }),
                    ...(override.examples !== undefined && { examples: override.examples }),
                };
            }),
        }));

        return {
            ...product,
            productInfo: {
                ...product.productInfo,
                [OVERRIDE_LOCALE]: {
                    ...localizedInfo,
                    salesOfferChecklist: {
                        ...localizedInfo.salesOfferChecklist,
                        checklistSections: updatedSections,
                    },
                },
            },
        };
    });
}
